'use client'

import * as React from 'react'
import { Step, TeamOnboarding } from '../team-onboarding'
import { useSearchParams } from 'next/navigation'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'
import { gsap } from 'lib/gsap'
import { Dialog, DialogContent } from '~/sotano-kit/primitives/dialog'

import { useGsapContext } from '~/hooks/use-gsap-context'
import { TeamForm } from '../team-form'
import { isUserTeam } from '@basehub/data/replicache/mutators/_public-types'
import { DialogOverlay } from '@radix-ui/react-dialog'

interface TeamCreateFormContext {
  prevStep: Step | null
  step: Step | null
  setStep: React.Dispatch<React.SetStateAction<Step | null>>
  onSkip: (e?: React.MouseEvent, action?: 'push' | 'replace') => void
}

export const teamCreateContext =
  React.createContext<TeamCreateFormContext | null>(null)

export function useTeamCreateFormContext() {
  const ctx = React.useContext(teamCreateContext) as TeamCreateFormContext
  if (!ctx) {
    throw new Error(
      'useTeamCreateFormContext must be used within a TeamCreateProvider'
    )
  }
  return ctx
}

export function TeamCreateProvider({ children }: React.PropsWithChildren) {
  const searchParams = useSearchParams()

  const { team } = useSelectedTeam()

  const [step, setStep] = React.useState<Step | null>(null)
  const [prevStep, setPrevStep] = React.useState<Step | null>(null)

  const isOnboarding = searchParams.get('onboarding') === 'true'

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('create-team') === 'true') {
      setStep('form')
    } else if (searchParams.get('alpha-team') === 'true') {
      setStep('alpha-form')
    }
  }, [])

  React.useLayoutEffect(() => {
    if (!team) return

    // We close the onboarding if the team was already onboarded / used
    if (
      isOnboarding &&
      isUserTeam(team) &&
      (team.members.length > 1 || team.repoIds?.length > 0)
    ) {
      window.history.replaceState({}, '', window.location.pathname)
    } else if (isOnboarding) {
      setStep((prevState) => {
        if (!prevState) return 'jb'
        return prevState
      })
    }
  }, [isOnboarding, team])

  const onSkip = (e?: React.MouseEvent, action?: 'push' | 'replace') => {
    if (e) e.preventDefault()
    if (action === 'push') {
      window.history.replaceState({}, '', window.location.pathname)
    } else window.history.replaceState({}, '', window.location.pathname)
    setStep(null)
  }

  const prevHeightRef = React.useRef<number | null>(null)
  const dialogContentRef = React.useRef<HTMLDivElement>(null)

  useGsapContext(() => {
    if (prevHeightRef.current && dialogContentRef.current) {
      const nextStepDiv =
        dialogContentRef.current.querySelector<HTMLDivElement>(
          'div[data-active="true"]'
        )

      if (!nextStepDiv) return
      const nextStepHeight = gsap.getProperty(nextStepDiv, 'height')

      gsap.fromTo(
        dialogContentRef.current,
        { height: prevHeightRef.current },
        {
          height: nextStepHeight,
          onComplete: () => {
            if (!dialogContentRef.current) return
            dialogContentRef.current.style.height = 'auto'
          },
          ease: 'power1.inOut',
          duration: 0.22,
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- so it updates per step
  }, [step])

  const changeStep = (s: Step) => {
    if (dialogContentRef.current) {
      const activeStepWrapper =
        dialogContentRef.current.querySelector<HTMLDivElement>(
          'div[data-active="true"]'
        )
      const height = gsap.getProperty(activeStepWrapper, 'height') ?? 0
      if (typeof height === 'string') prevHeightRef.current = parseInt(height)
      else prevHeightRef.current = height
    }
    setPrevStep(step)
    setStep(s)
  }

  return (
    <teamCreateContext.Provider
      value={{
        prevStep,
        step,
        setStep,
        onSkip,
      }}
    >
      <Dialog
        open={step !== null}
        onOpenChange={(o) => {
          if (!o) onSkip()
        }}
      >
        <DialogOverlay />
        <div className="relative">
          <DialogContent
            className="sm:!max-w-[600px] overflow-hidden origin-center focus-visible:outline-none"
            ref={dialogContentRef}
          >
            {!step ? null : step === 'form' ? (
              <TeamForm
                handleClose={() => {
                  setStep(null)
                  window.history.replaceState({}, '', window.location.pathname)
                }}
              />
            ) : (
              <TeamOnboarding
                currentStep={step}
                prevStep={prevStep}
                onStepChange={changeStep}
                onSkipSteps={onSkip}
              />
            )}
          </DialogContent>
        </div>
      </Dialog>
      {children}
    </teamCreateContext.Provider>
  )
}
